import { useState, useEffect } from "react";
import { EditorModeToggle } from "@/lib/siteContent";
import NotificationBell from "@/components/NotificationBell";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { CATEGORIES } from "@/data/categories";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import UserAvatar from "@/components/UserAvatar";
import { catPath } from "@/lib/paths";

const LOGO_URL = "/brand/tiertcg-logo.webp";
const NAV_ORDER = ["stores", "events", "creators", "vending"];
const NAV_CATEGORIES = NAV_ORDER
  .map((id) => CATEGORIES.find((c) => c.id === id))
  .filter(Boolean);
// Header shows the main sections only; vending / vote / submit live in the menu.
const HEADER_CATEGORIES = NAV_CATEGORIES.filter((c) => c.id !== "vending");

const navCls = ({ isActive }, accent = false) =>
  `px-2.5 xl:px-3 py-1.5 text-xs uppercase tracking-[0.16em] font-semibold rounded-sm whitespace-nowrap transition-colors duration-200 ${
    isActive
      ? "text-amber-300 bg-amber-500/10"
      : accent
      ? "text-amber-400/80 hover:text-amber-200 font-bold"
      : "text-stone-400 hover:text-amber-200"
  }`;

export default function Header() {
  const { t, lang, setLang } = useI18n();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  // Patch 33: keep the drawer mounted while it pops out, like every other pop-up.
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (open) { setShown(true); return undefined; }
    const id = setTimeout(() => setShown(false), 150);
    return () => clearTimeout(id);
  }, [open]);
  const location = useLocation();
  // patch 35: keyboard — Escape closes the menu and focus goes back to the ☰ button
  useEffect(() => {
    if (!open) return undefined;
    const on = (e) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      document.querySelector('[data-testid="mobile-menu-button"]')?.focus();
    };
    window.addEventListener("keydown", on);
    const id = setTimeout(() => document.querySelector('[data-testid="mobile-menu-drawer"] a, [data-testid="mobile-menu-drawer"] button')?.focus({ preventScroll: true }), 60);
    return () => { window.removeEventListener("keydown", on); clearTimeout(id); };
  }, [open]);

  // Close drawer on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  // Lock scroll when drawer open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-40 bg-[#0A0A0C]/85 backdrop-blur-md border-b border-amber-500/15"
    >
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 h-16 flex items-center justify-between gap-6">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-3 group shrink-0">
          <img src={LOGO_URL} alt="tierTCG" className="h-10 sm:h-8 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-0.5 min-w-0" data-testid="category-nav">
          {HEADER_CATEGORIES.map((c) => (
            <NavLink key={c.id} to={catPath(c.id)} data-testid={`nav-category-${c.id}`} className={navCls}>
              {c.short[lang] || c.short.en}
            </NavLink>
          ))}
          <span className="mx-1 h-4 w-px bg-amber-500/20" />
          <NavLink to="/finder" data-testid="nav-finder" className={(a) => navCls(a, true)}>
            {t.nav.finder}
          </NavLink>
          <NavLink to="/top" data-testid="nav-top" className={navCls}>
            {t.nav.top}
          </NavLink>
        </nav>

        <div className="flex items-center gap-2">
          {/* Auth */}
          {user ? (
            <div className="flex items-stretch h-9 border border-amber-500/25 rounded-sm" data-testid="header-user-chip">
              <NotificationBell className="border-r border-amber-500/25" />
              {user.is_admin && (
                <Link
                  to="/admin/dashboard"
                  data-testid="header-admin-link"
                  className="hidden xl:inline-flex items-center px-2.5 text-[11px] uppercase tracking-[0.18em] font-bold text-amber-300 bg-amber-500/15 hover:bg-amber-500/25 border-r border-amber-500/25"
                >
                  Admin
                </Link>
              )}
              {user.is_admin && <EditorModeToggle className="hidden xl:inline-flex border-r border-amber-500/25" />}
              <Link
                to={`/u/${user.username}`}
                data-testid="header-profile-link"
                className="inline-flex items-center gap-2 px-2 sm:px-2.5 text-[11px] uppercase tracking-[0.18em] font-bold text-amber-200 hover:bg-amber-500/10 leading-none"
              >
                <UserAvatar avatar={user.avatar} name={user.username} size={20} />
                <span className="sm:hidden leading-none">{t.nav.profile}</span>
                <span translate="no" className="hidden sm:inline leading-none max-w-[9rem] truncate">@{user.username}</span>
              </Link>
              <button
                type="button"
                onClick={logout}
                data-testid="header-logout"
                className="hidden xl:inline-flex items-center px-2 text-[11px] uppercase tracking-[0.18em] font-bold text-stone-400 hover:text-amber-200 border-l border-amber-500/25"
              >
                {t.auth.logout}
              </button>
            </div>
          ) : (
            <>
            <Link
              to="/login"
              data-testid="header-login-mobile"
              className="btn-gold sm:hidden inline-flex items-center h-9 px-3 rounded-sm text-[11px] uppercase tracking-[0.18em] font-bold"
            >
              {t.auth.sign_in}
            </Link>
            <div className="hidden sm:flex items-stretch h-9 border border-amber-500/25 rounded-sm overflow-hidden">
              <Link
                to="/login"
                data-testid="header-login-link"
                className="inline-flex items-center px-2.5 text-[11px] uppercase tracking-[0.18em] font-bold text-stone-400 hover:text-amber-200"
              >
                {t.auth.sign_in}
              </Link>
              <Link
                to="/register"
                data-testid="header-register-link"
                className="inline-flex items-center px-2.5 text-[11px] uppercase tracking-[0.18em] font-bold text-amber-200 bg-amber-500/10 border-l border-amber-500/25 hover:bg-amber-500/20"
              >
                {t.auth.join}
              </Link>
            </div>
            </>
          )}

          <div
            data-testid="lang-switcher"
            className="hidden sm:flex items-stretch h-9 border border-amber-500/25 rounded-sm overflow-hidden"
          >
            <button
              type="button"
              onClick={() => setLang("en")}
              data-testid="lang-en"
              aria-pressed={lang === "en"}
              className={`inline-flex items-center px-2.5 text-[11px] uppercase tracking-[0.18em] font-bold transition-colors duration-200 ${
                lang === "en" ? "bg-amber-500/20 text-amber-200" : "text-stone-400 hover:text-amber-200"
              }`}
            >
              EN
            </button>
            <button
              type="button"
              onClick={() => setLang("pt")}
              data-testid="lang-pt"
              aria-pressed={lang === "pt"}
              className={`inline-flex items-center px-2.5 text-[11px] uppercase tracking-[0.18em] font-bold transition-colors duration-200 ${
                lang === "pt" ? "bg-amber-500/20 text-amber-200" : "text-stone-400 hover:text-amber-200"
              }`}
            >
              PT
            </button>
          </div>

          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-button"
            aria-expanded={open}
            aria-label="Menu"
            className="inline-flex items-center justify-center w-9 h-9 rounded-sm border border-amber-500/25 text-amber-300 hover:bg-amber-500/10 transition-colors duration-200"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {shown && (
        <div
          data-testid={open ? "mobile-menu-drawer" : undefined}
          data-state={open ? "open" : "closed"}
          style={open ? undefined : { pointerEvents: "none" }}
          className="tier-pop menu-pop absolute left-0 right-0 lg:left-auto lg:right-10 lg:w-80 lg:border lg:rounded-sm top-16 bg-[#0A0A0C] border-t border-amber-500/20 shadow-[0_20px_40px_-10px_rgba(0,0,0,0.8)] max-h-[calc(100vh-4rem)] overflow-y-auto"
        >
          <nav className="px-5 py-4 flex flex-col gap-1">
            {NAV_CATEGORIES.map((c) => (
              <NavLink
                key={c.id}
                to={catPath(c.id)}
                data-testid={`nav-category-mobile-${c.id}`}
                className={({ isActive }) =>
                  `flex items-center justify-between px-4 py-3 rounded-sm border transition-colors duration-200 ${
                    isActive
                      ? "border-amber-400/60 bg-amber-500/10 text-amber-200"
                      : "border-amber-500/10 text-stone-100 hover:border-amber-500/30 hover:text-amber-200"
                  }`
                }
              >
                <span className="text-xs uppercase tracking-[0.22em] font-bold">
                  {c.short[lang] || c.short.en}
                </span>
              </NavLink>
            ))}
            <div className="h-px bg-amber-500/15 my-2" />
            <NavLink
              to="/vote"
              data-testid="nav-vote-mobile"
              className={({ isActive }) =>
                `flex items-center justify-between px-4 py-3 rounded-sm border transition-colors duration-200 ${
                  isActive
                    ? "border-amber-400/60 bg-amber-500/10 text-amber-200"
                    : "border-amber-500/20 text-amber-400 hover:border-amber-400/60 hover:text-amber-200"
                }`
              }
            >
              <span className="text-xs uppercase tracking-[0.22em] font-bold">
                {t.vote.cta}
              </span>
            </NavLink>
            <NavLink
              to="/finder"
              data-testid="nav-finder-mobile"
              className={({ isActive }) =>
                `flex items-center justify-between px-4 py-3 rounded-sm border transition-colors duration-200 ${
                  isActive
                    ? "border-amber-400/60 bg-amber-500/10 text-amber-200"
                    : "border-amber-500/20 text-amber-400 hover:border-amber-400/60 hover:text-amber-200"
                }`
              }
            >
              <span className="text-xs uppercase tracking-[0.22em] font-bold">
                {t.nav.finder}
              </span>
            </NavLink>
            <NavLink
              to="/submit"
              data-testid="nav-submit-mobile"
              className={({ isActive }) =>
                `flex items-center justify-between px-4 py-3 rounded-sm border transition-colors duration-200 ${
                  isActive
                    ? "border-amber-400/60 bg-amber-500/10 text-amber-200"
                    : "border-amber-500/20 text-stone-300 hover:border-amber-500/40 hover:text-amber-200"
                }`
              }
            >
              <span className="text-xs uppercase tracking-[0.22em] font-bold">
                {t.nav.submit}
              </span>
            </NavLink>
            <NavLink
              to="/top"
              data-testid="nav-top-mobile"
              className={({ isActive }) =>
                `flex items-center justify-between px-4 py-3 rounded-sm border transition-colors duration-200 ${
                  isActive
                    ? "border-amber-400/60 bg-amber-500/10 text-amber-200"
                    : "border-amber-500/20 text-stone-300 hover:border-amber-500/40 hover:text-amber-200"
                }`
              }
            >
              <span className="text-xs uppercase tracking-[0.22em] font-bold">{t.nav.top}</span>
            </NavLink>
            {/* Language (mobile only — on larger screens it sits in the header) */}
            <div
              data-testid="lang-switcher-mobile"
              className="sm:hidden flex items-center justify-between px-4 py-2.5 rounded-sm border border-amber-500/20"
            >
              <span className="text-xs uppercase tracking-[0.22em] font-bold text-stone-300">{t.nav.language}</span>
              <div className="flex items-center border border-amber-500/25 rounded-sm overflow-hidden">
                {["en", "pt"].map((l) => (
                  <button
                    key={l}
                    type="button"
                    onClick={() => setLang(l)}
                    data-testid={`lang-${l}-mobile`}
                    aria-pressed={lang === l}
                    className={`px-3 py-1 text-[11px] uppercase tracking-[0.18em] font-bold transition-colors duration-200 ${
                      lang === l ? "bg-amber-500/20 text-amber-200" : "text-stone-400 hover:text-amber-200"
                    }`}
                  >
                    {l.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
            <div className="h-px bg-amber-500/15 my-2" />
            {user ? (
              <>
                {user.is_admin && (
                  <NavLink
                    to="/admin/dashboard"
                    data-testid="nav-admin-mobile"
                    className="xl:hidden flex items-center justify-between px-4 py-3 rounded-sm border border-amber-400/40 bg-amber-500/10 text-amber-300"
                  >
                    <span className="text-xs uppercase tracking-[0.22em] font-bold">Admin</span>
                  </NavLink>
                )}
                <NavLink
                  to={`/u/${user.username}`}
                  data-testid="nav-profile-mobile"
                  className="flex items-center justify-between px-4 py-3 rounded-sm border border-amber-500/20 text-amber-200 hover:border-amber-400/60"
                >
                  <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold">
                    <UserAvatar avatar={user.avatar} name={user.username} size={20} /> @{user.username}
                  </span>
                </NavLink>
                {!user.is_admin && (
                  <NavLink
                    to="/tickets"
                    data-testid="nav-tickets-mobile"
                    className="flex items-center justify-between px-4 py-3 rounded-sm border border-amber-500/20 text-stone-300 hover:text-amber-200"
                  >
                    <span className="text-xs uppercase tracking-[0.22em] font-bold">{t.tickets.my}</span>
                  </NavLink>
                )}
                <button
                  type="button"
                  onClick={logout}
                  data-testid="nav-logout-mobile"
                  className="flex items-center justify-between px-4 py-3 rounded-sm border border-amber-500/20 text-stone-300 hover:text-amber-200"
                >
                  <span className="text-xs uppercase tracking-[0.22em] font-bold">
                    {t.auth.logout}
                  </span>
                </button>
              </>
            ) : (
              <>
                <NavLink
                  to="/login"
                  data-testid="nav-login-mobile"
                  className="flex items-center justify-between px-4 py-3 rounded-sm border border-amber-500/20 text-stone-300 hover:text-amber-200"
                >
                  <span className="text-xs uppercase tracking-[0.22em] font-bold">
                    {t.auth.sign_in}
                  </span>
                </NavLink>
                <NavLink
                  to="/register"
                  data-testid="nav-register-mobile"
                  className="flex items-center justify-between px-4 py-3 rounded-sm border border-amber-400/60 bg-amber-500/10 text-amber-200"
                >
                  <span className="text-xs uppercase tracking-[0.22em] font-bold">
                    {t.auth.join}
                  </span>
                </NavLink>
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
